/*
 * Назначение: страница «Дашборд».
 * Функционал: KPI текущего проекта, график «диалоги/заявки по дням»,
 * здоровье ИИ (модели, успехи, средняя задержка, fallback'и), последние
 * заявки. Все данные — из /api/admin/dashboard.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock,
  Loader2,
  MessageSquare,
  TrendingDown,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import { useNavigate } from "react-router";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { dashboardApi } from "../../lib/admin-api";
import {
  CHANNEL_LABEL,
  formatRelative,
  formatShortDate,
  LEAD_STATUS_LABEL,
} from "../../lib/format";
import { useProject } from "../components/project/project-provider";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const KPI_ICONS = [MessageSquare, ClipboardList, UserCheck, TrendingUp];

export function DashboardPage() {
  const { current, currentSlug } = useProject();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(7);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["dashboard", currentSlug, period],
    queryFn: () => dashboardApi.overview(period),
    enabled: !!currentSlug,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Обзор</h2>
          <p className="text-sm text-muted-foreground">
            Статистика по проекту {current ? `«${current.name}»` : ""}
          </p>
        </div>
        <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Сегодня</SelectItem>
            <SelectItem value="7">Последние 7 дней</SelectItem>
            <SelectItem value="30">Последние 30 дней</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          Загружаем данные…
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="py-8 flex flex-col items-center text-center gap-3">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <p className="font-medium">Не удалось загрузить дашборд</p>
            <Button variant="outline" onClick={() => refetch()}>
              Попробовать снова
            </Button>
          </CardContent>
        </Card>
      )}

      {data && !isLoading && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.kpi.map((kpi, i) => {
              const Icon = KPI_ICONS[i] ?? TrendingUp;
              const isPositive = kpi.direction === "up";
              const isNegative = kpi.direction === "down";
              const TrendIcon = isPositive ? TrendingUp : TrendingDown;
              const colorClass = isPositive
                ? "text-success"
                : isNegative
                  ? "text-destructive"
                  : "text-muted-foreground";
              return (
                <Card key={kpi.title}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {kpi.title}
                    </CardTitle>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{kpi.value}</div>
                    <div className="flex items-center gap-1 mt-1">
                      {kpi.direction !== "flat" && (
                        <TrendIcon className={`h-3 w-3 ${colorClass}`} />
                      )}
                      <span className={`text-xs font-medium ${colorClass}`}>
                        {kpi.change === null
                          ? "первые данные"
                          : `${kpi.change > 0 ? "+" : ""}${kpi.change}%`}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        к предыдущему периоду
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Charts and AI health */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Динамика диалогов и заявок</CardTitle>
                <CardDescription>По дням за выбранный период</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart
                    data={data.chart.map((p) => ({
                      ...p,
                      label: formatShortDate(p.date),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="dialogs" fill="#5b4cdb" name="Диалоги" />
                    <Line
                      type="monotone"
                      dataKey="leads"
                      stroke="#16a34a"
                      strokeWidth={2}
                      name="Заявки"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Здоровье ИИ</CardTitle>
                <CardDescription>
                  Статус моделей и производительность за период
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.ai_health.models.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Пока нет вызовов моделей за выбранный период.
                  </p>
                )}
                {data.ai_health.models.map((m) => {
                  const isPrimary = m.role === "primary";
                  const ok = m.success_rate >= 0.95;
                  return (
                    <div key={m.model} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {ok ? (
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        ) : (
                          <Clock className="h-5 w-5 text-warning" />
                        )}
                        <div>
                          <p className="font-medium">
                            {isPrimary ? "Основная модель" : "Резервная"}
                          </p>
                          <p className="text-sm text-muted-foreground">{m.model}</p>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          ok
                            ? "bg-success/10 text-success border-success/20"
                            : "bg-warning/10 text-warning border-warning/20"
                        }
                      >
                        {m.calls > 0
                          ? `${Math.round(m.success_rate * 100)}% (${m.calls})`
                          : "не использовалась"}
                      </Badge>
                    </div>
                  );
                })}

                <div className="pt-4 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Успешных ответов</span>
                    <span className="font-medium">
                      {Math.round(data.ai_health.success_rate * 100)}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Средняя задержка</span>
                    <span className="font-medium">
                      {data.ai_health.avg_latency_ms !== null
                        ? `${(data.ai_health.avg_latency_ms / 1000).toFixed(2)} сек`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Переключений на резерв</span>
                    <span className="font-medium">{data.ai_health.fallback_count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent leads + tops */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Последние заявки</CardTitle>
                  <CardDescription>Новые обращения клиентов</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/leads")}>
                  Все заявки
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {data.recent_leads.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4">
                    Пока нет ни одной заявки. Они появятся, когда клиенты начнут
                    общаться с ИИ-продавцом.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data.recent_leads.map((lead) => (
                      <button
                        type="button"
                        key={lead.id}
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        className="w-full text-left flex items-center justify-between pb-4 border-b last:border-0 last:pb-0 hover:bg-muted/40 rounded-md px-2 -mx-2"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{lead.name || "Без имени"}</p>
                            {lead.status === "new" && (
                              <Badge variant="destructive" className="h-5">
                                {LEAD_STATUS_LABEL.new}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {lead.service || "Услуга не указана"}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {CHANNEL_LABEL[lead.channel]}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatRelative(lead.created_at)}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Частые вопросы</CardTitle>
                  <CardDescription>Топ-5 за период</CardDescription>
                </CardHeader>
                <CardContent>
                  {data.top_questions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Появится, когда наберётся история диалогов.
                    </p>
                  ) : (
                    <ol className="space-y-2 text-sm">
                      {data.top_questions.map((q, i) => (
                        <li key={i} className="flex justify-between gap-3">
                          <span>{q.text}</span>
                          <span className="text-muted-foreground">{q.count}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Частые возражения</CardTitle>
                  <CardDescription>Топ-5 за период</CardDescription>
                </CardHeader>
                <CardContent>
                  {data.top_objections.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Появится, когда наберётся история диалогов.
                    </p>
                  ) : (
                    <ol className="space-y-2 text-sm">
                      {data.top_objections.map((q, i) => (
                        <li key={i} className="flex justify-between gap-3">
                          <span>{q.text}</span>
                          <span className="text-muted-foreground">{q.count}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
